import { useQuery } from '@tanstack/react-query';

import { CompanyMessage } from '../../../../types/CompanyMessage';
import { VesselMessage } from '../../../../types/VesselMessage';
import { queries } from '../../../common/api/queryKeys/queries';
import { useRole } from '../../../common/hooks/useRole';

interface UseContractVesselAccessDataProps {
    customerGroupId?: string;
    customerIds: string[];
    enabled?: boolean;
}

export const useContractVesselAccessData = ({
    customerGroupId,
    customerIds,
    enabled = true,
}: UseContractVesselAccessDataProps) => {
    const role = useRole();
    const roleCustomers: CompanyMessage[] = role.getCompanies();
    const isSuperAdmin = role.isSuperAdmin();
    const groupCustomersQuery = useQuery({
        ...queries.customers.list({ groupId: customerGroupId }),
        enabled: enabled && isSuperAdmin && !!customerGroupId,
        staleTime: Infinity,
    });
    const availableCustomers = isSuperAdmin ? groupCustomersQuery.data ?? [] : roleCustomers;
    const allowedCustomerIds = new Set(availableCustomers.map((customer) => customer.id));
    const effectiveCustomerIds = customerIds.length
        ? customerIds.filter((customerId) => allowedCustomerIds.has(customerId))
        : [...allowedCustomerIds];
    const vesselsQuery = useQuery({
        ...queries.vessels.list({ customerIds: effectiveCustomerIds, active: true }),
        enabled:
            enabled &&
            effectiveCustomerIds.length > 0 &&
            (!isSuperAdmin || customerIds.length > 0 || groupCustomersQuery.isSuccess),
        select: (vessels: VesselMessage[]) =>
            [...vessels].sort((vessel, otherVessel) => vessel.name.localeCompare(otherVessel.name)),
    });

    const vessels = vesselsQuery.data ?? [];
    const customers = availableCustomers
        .filter((customer) => effectiveCustomerIds.includes(customer.id))
        .sort((customer, otherCustomer) => customer.name.localeCompare(otherCustomer.name));

    return {
        customers,
        vessels,
        isLoading: groupCustomersQuery.isLoading || vesselsQuery.isLoading,
        isError: groupCustomersQuery.isError || vesselsQuery.isError,
    };
};
